import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { API_BASE_URL, API_URL } from '../config/api';

const PipelineRequestForm = ({ onClose, onSuccess, existingPipeline = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    compensation_range: '',
    location: '',
    job_description_file_url: '',
    job_description_file_name: ''
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Pre-fill form if editing existing pipeline
  useEffect(() => {
    if (existingPipeline) {
      // Combine description and requirements
      let combinedDescription = existingPipeline.description || '';
      if (existingPipeline.requirements) {
        if (combinedDescription) {
          combinedDescription += '<h3>Requirements & Qualifications</h3>' + existingPipeline.requirements;
        } else {
          combinedDescription = existingPipeline.requirements;
        }
      }
      
      setFormData({
        name: existingPipeline.name || '',
        description: combinedDescription,
        compensation_range: existingPipeline.compensation_range || '',
        location: existingPipeline.location || '',
        job_description_file_url: existingPipeline.job_description_file_url || '',
        job_description_file_name: existingPipeline.job_description_file_name || ''
      });
    }
  }, [existingPipeline]);

  const processFile = async (file) => {
    if (!file) return;

    // Check file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a PDF or Word document');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setUploadedFile(file);
    setError('');
    setParsing(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${API_URL}/client/parse-job-description`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Auto-fill form with parsed data
      if (response.data) {
        console.log('📄 Parsed response from backend:', response.data);
        console.log('📄 File URL:', response.data.file_url);
        console.log('📄 File Name:', response.data.file_name);
        
        // Backend now returns HTML, so just combine it
        let combinedDescription = '';
        if (response.data.description) {
          combinedDescription = response.data.description;
        }
        if (response.data.requirements) {
          if (combinedDescription) {
            combinedDescription += response.data.requirements;
          } else {
            combinedDescription = response.data.requirements;
          }
        }
        
        const newFormData = {
          name: response.data.position_title || '',
          location: response.data.location || '',
          compensation_range: response.data.compensation || '',
          description: combinedDescription || '',
          job_description_file_url: response.data.file_url || '',
          job_description_file_name: response.data.file_name || ''
        };
        
        console.log('📄 New form data being set:', newFormData);
        
        setFormData(newFormData);
        
        console.log('📄 FormData after setting:', {
          file_url: response.data.file_url,
          file_name: response.data.file_name,
          name: response.data.position_title,
          location: response.data.location,
          compensation_range: response.data.compensation
        });
      }
    } catch (err) {
      console.error('Error parsing file:', err);
      setError('Could not parse the file. Please fill in the details manually.');
    } finally {
      setParsing(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    await processFile(file);
  };

  const handleDeleteFile = () => {
    setFormData(prev => ({
      ...prev,
      job_description_file_url: '',
      job_description_file_name: ''
    }));
    setUploadedFile(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    await processFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('💾 Submitting form with formData:', {
      name: formData.name,
      file_url: formData.job_description_file_url,
      file_name: formData.job_description_file_name
    });
    setError('');

    if (!formData.name) {
      setError('Position title is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const user = JSON.parse(localStorage.getItem('user'));
      
      if (existingPipeline) {
        // Update existing draft
        await axios.put(`${API_URL}/pipelines/${existingPipeline.id}`, {
          name: formData.name,
          description: formData.description,
          requirements: '', // Clear requirements - using combined field now
          compensation_range: formData.compensation_range,
          location: formData.location,
          job_description_file_url: formData.job_description_file_url,
          job_description_file_name: formData.job_description_file_name
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        alert('Draft updated successfully!');
      } else {
        // Create new pipeline
        await axios.post(`${API_URL}/pipelines`, {
          name: formData.name,
          firm_id: user.firm_id,
          description: formData.description,
          requirements: '', // Clear requirements - using combined field now
          compensation_range: formData.compensation_range,
          location: formData.location,
          job_description_file_url: formData.job_description_file_url,
          job_description_file_name: formData.job_description_file_name
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        alert('New search created successfully!');
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error saving pipeline:', err);
      setError(err.response?.data?.error || 'Failed to save pipeline');
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!existingPipeline;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <style>{`
        .quill {
          background: white;
        }
        .ql-container {
          min-height: 150px;
          font-size: 14px;
          border-bottom-left-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
        }
        .ql-toolbar {
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          background: #f9fafb;
        }
        .ql-editor {
          min-height: 150px;
        }
        .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
      `}</style>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">
            {isEditing ? 'Edit Draft Search' : 'Create New Search'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* File Upload Section */}
          {formData.job_description_file_url ? (
            /* Show existing file with option to replace */
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <p className="font-medium text-gray-900">Attached File</p>
                    <p className="text-sm text-gray-600">{formData.job_description_file_name || 'Document'}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <a
                    href={`${API_BASE_URL}${formData.job_description_file_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    View
                  </a>
                  <button
                    type="button"
                    onClick={handleDeleteFile}
                    className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
              {parsing && (
                <div className="mt-3 flex items-center justify-center gap-2 text-sm text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  Analyzing new document...
                </div>
              )}
            </div>
          ) : (
            /* Show upload area if no file */
            <div 
              className={`bg-blue-50 border-2 border-dashed rounded-lg p-6 transition-colors ${
                isDragging 
                  ? 'border-blue-500 bg-blue-100' 
                  : 'border-blue-300'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="text-center">
                <svg className="w-12 h-12 text-blue-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <label className="cursor-pointer">
                  <span className="text-blue-600 hover:text-blue-700 font-medium">
                    {isDragging ? 'Drop file here' : 'Click to upload or drag and drop'}
                  </span>
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    disabled={parsing}
                  />
                </label>
                <p className="text-sm text-gray-600 mt-2">
                  PDF or Word document (max 10MB)
                </p>
                
                {uploadedFile && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-sm text-gray-700">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {uploadedFile.name}
                  </div>
                )}

                {parsing && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-sm text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  Analyzing document...
                </div>
              )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Position Title *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Chief Financial Officer"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData(prev => ({...prev, location: e.target.value}))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., New York, NY or Remote"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compensation Range
            </label>
            <input
              type="text"
              value={formData.compensation_range}
              onChange={(e) => setFormData(prev => ({...prev, compensation_range: e.target.value}))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., $200,000 - $250,000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Description
            </label>
            <ReactQuill
              value={formData.description}
              onChange={(value) => setFormData(prev => ({...prev, description: value}))}
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  ['link'],
                  ['clean']
                ]
              }}
              className="bg-white"
              placeholder="Describe the role, responsibilities, requirements, and qualifications..."
            />
          </div>

          {!isEditing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">This will create a draft search</p>
                  <p>Review and edit your draft, then submit it when ready for the team to begin work.</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || parsing}
              className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                saving || parsing
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {saving ? 'Saving...' : (isEditing ? 'Update Draft' : 'Create Draft')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PipelineRequestForm;
